import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { materialDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import { sections } from '../data/documentSections';

interface DocumentContentProps {
  sectionId: string;
}

const DocumentContent = ({ sectionId }: DocumentContentProps) => {
  const [section, setSection] = useState(sections.find(s => s.id === sectionId));

  useEffect(() => {
    setSection(sections.find(s => s.id === sectionId));
  }, [sectionId]);

  if (!section) {
    return (
      <Box sx={{ padding: 3 }}>
        <Typography variant="h5">Section not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 2 }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code({ className, children }) {
            const match = /language-(\w+)/.exec(className || '');
            return match ? (
              <SyntaxHighlighter
                style={materialDark}
                language={match[1]}
                PreTag="div"
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code className={className}>
                {children}
              </code>
            );
          },
          table({ children }) {
            return (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  {children}
                </table>
              </div>
            );
          },
          th({ children }) {
            return (
              <th
                style={{
                  border: '1px solid #ddd',
                  padding: '8px',
                  backgroundColor: '#f2f2f2',
                  textAlign: 'left'
                }}
              >
                {children}
              </th>
            );
          },
          td({ children }) {
            return (
              <td
                style={{
                  border: '1px solid #ddd',
                  padding: '8px'
                }}
              >
                {children}
              </td>
            );
          },
          h1({ children }) {
            return <Typography variant="h4" gutterBottom component="h1">{children}</Typography>;
          },
          h2({ children }) {
            return <Typography variant="h5" gutterBottom sx={{ mt: 4 }} component="h2">{children}</Typography>;
          },
          h3({ children }) {
            return <Typography variant="h6" gutterBottom sx={{ mt: 3 }} component="h3">{children}</Typography>;
          }
        }}
      >
        {section.content}
      </ReactMarkdown>
    </Box>
  );
};

export default DocumentContent; 